import { Preferences } from '@capacitor/preferences';

// ─── Platform Detection ──────────────────────────────────────────────

/** Check if running in a native Capacitor environment */
export function isNativePlatform(): boolean {
  try {
    return !!(window as any).Capacitor?.isNativePlatform?.();
  } catch {
    return false;
  }
}

/** Get the current platform: ios | android | web */
export function getPlatform(): 'ios' | 'android' | 'web' {
  try {
    const p = (window as any).Capacitor?.getPlatform?.();
    return p || 'web';
  } catch {
    return 'web';
  }
}

// ─── Camera Capture ──────────────────────────────────────────────────

export interface CameraOptions {
  quality?: number;
  source?: 'camera' | 'gallery';
  allowMultiple?: boolean;
}

export interface CapturedPhoto {
  path?: string;
  webPath?: string;
  base64?: string;
  format: string;
  exif?: Record<string, any>;
}

/** Capture a photo using native camera or web fallback */
export async function capturePhoto(options: CameraOptions = {}): Promise<CapturedPhoto | null> {
  try {
    if (isNativePlatform()) {
      const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');
      const result = await Camera.getPhoto({
        quality: options.quality ?? 90,
        source: options.source === 'gallery' ? CameraSource.Photos : CameraSource.Camera,
        resultType: CameraResultType.DataUrl,
        allowEditing: false,
      });
      return {
        webPath: result.webPath,
        base64: result.dataUrl,
        format: result.format,
        exif: result.exif as any,
      };
    }

    // Web fallback: use HTML file input
    const file = await webCameraCapture();
    if (!file) return null;

    return {
      base64: await fileToBase64(file),
      format: file.type,
    };
  } catch (error) {
    console.error('Camera capture error:', error);
    return null;
  }
}

function webCameraCapture(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment';
    input.onchange = () => resolve(input.files?.[0] ?? null);
    input.oncancel = () => resolve(null);
    input.click();
  });
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ─── Barcode / QR Scanner ────────────────────────────────────────────

export interface ScanResult {
  value: string;
  format?: string;
}

/** Scan a barcode or QR code using live camera */
export async function scanBarcode(): Promise<ScanResult | null> {
  try {
    if (isNativePlatform()) {
      const { BarcodeScanner, BarcodeFormat, LensFacing } = await import(
        '@capacitor-mlkit/barcode-scanning'
      );

      const { camera } = await BarcodeScanner.checkPermissions();
      if (camera !== 'granted') {
        const { camera: granted } = await BarcodeScanner.requestPermissions();
        if (granted !== 'granted') return null;
      }

      document.body.classList.add('barcode-scanner-active');

      return new Promise((resolve) => {
        let resolved = false;

        const cleanup = async () => {
          if (resolved) return;
          resolved = true;
          document.body.classList.remove('barcode-scanner-active');
          await BarcodeScanner.removeAllListeners();
          await BarcodeScanner.stopScan().catch(() => {});
        };

        BarcodeScanner.addListener('barcodesScanned', async ({ barcodes }) => {
          if (resolved) return;
          if (barcodes.length > 0) {
            const b = barcodes[0];
            await cleanup();
            resolve({ value: b.rawValue ?? '', format: b.format ?? undefined });
          }
        });

        BarcodeScanner.addListener('scanError', async () => {
          await cleanup();
          resolve(null);
        });

        BarcodeScanner.startScan({
          formats: [BarcodeFormat.QrCode, BarcodeFormat.Code128, BarcodeFormat.Code39],
          lensFacing: LensFacing.Back,
        });

        setTimeout(() => {
          cleanup().then(() => resolve(null));
        }, 60000);
      });
    }

    // Web fallback: HTML5 Barcode Detection API
    if ('BarcodeDetector' in window) {
      const detector = new (window as any).BarcodeDetector();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      const track = stream.getVideoTracks()[0];
      const imageCapture = new (window as any).ImageCapture(track);
      const bitmap = await imageCapture.grabFrame();
      const codes = await detector.detect(bitmap);
      track.stop();
      if (codes.length > 0) {
        return { value: codes[0].rawValue, format: codes[0].format };
      }
    }
    return null;
  } catch {
    return null;
  }
}

/** Start a live QR/barcode scan session. Returns cleanup function. */
export async function startLiveBarcodeScan(
  onResult: (result: ScanResult) => void,
  onError?: (error: string) => void,
): Promise<() => Promise<void>> {
  if (isNativePlatform()) {
    const { BarcodeScanner, BarcodeFormat, LensFacing } = await import(
      '@capacitor-mlkit/barcode-scanning'
    );

    const { camera } = await BarcodeScanner.checkPermissions();
    if (camera !== 'granted') {
      const { camera: granted } = await BarcodeScanner.requestPermissions();
      if (granted !== 'granted') {
        onError?.('Camera permission denied');
        return async () => {};
      }
    }

    document.body.classList.add('barcode-scanner-active');

    await BarcodeScanner.addListener('barcodesScanned', async ({ barcodes }) => {
      if (barcodes.length > 0) {
        const b = barcodes[0];
        onResult({ value: b.rawValue ?? '', format: b.format ?? undefined });
      }
    });

    await BarcodeScanner.addListener('scanError', async ({ message }) => {
      onError?.(message);
    });

    await BarcodeScanner.startScan({
      formats: [BarcodeFormat.QrCode, BarcodeFormat.Code128, BarcodeFormat.Code39],
      lensFacing: LensFacing.Back,
    });

    return async () => {
      document.body.classList.remove('barcode-scanner-active');
      await BarcodeScanner.removeAllListeners();
      await BarcodeScanner.stopScan().catch(() => {});
    };
  }

  // Web fallback: return no-op
  onError?.('Barcode scanning not supported on web');
  return async () => {};
}

// ─── NFC Scanning ────────────────────────────────────────────────────

export interface NfcTagResult {
  tagId: string;
  serialNumber?: string;
  tagType?: string;
  isWritable?: boolean;
  maxSize?: number;
  data?: string;
}

/** Scan an NFC tag */
export async function scanNfcTag(): Promise<NfcTagResult | null> {
  try {
    if (isNativePlatform()) {
      const { CapacitorNfc } = await import('@capgo/capacitor-nfc');

      const { supported } = await CapacitorNfc.isSupported();
      if (!supported) return null;

      await CapacitorNfc.startScanning({ invalidateAfterFirstRead: true });

      return new Promise((resolve) => {
        let resolved = false;
        CapacitorNfc.addListener('nfcEvent', (event) => {
          if (resolved) return;
          resolved = true;
          const tagId = event.tag.id
            ? event.tag.id
                .map((b: number) => b.toString(16).padStart(2, '0').toUpperCase())
                .join(':')
            : '';
          const records = event.tag.ndefMessage ?? [];
          const data = records
            .map((r: any) => new TextDecoder().decode(new Uint8Array(r.payload)))
            .join('');
          CapacitorNfc.stopScanning().catch(() => {});
          resolve({
            tagId: tagId || data,
            serialNumber: tagId || undefined,
            tagType: event.tag.type ?? undefined,
            isWritable: event.tag.isWritable ?? undefined,
            maxSize: event.tag.maxSize ?? undefined,
            data: data || undefined,
          });
        });
        setTimeout(() => {
          if (!resolved) {
            resolved = true;
            CapacitorNfc.stopScanning().catch(() => {});
            resolve(null);
          }
        }, 30000);
      });
    }

    // Web fallback: Web NFC API (Chrome Android only)
    if ('NDEFReader' in window) {
      const reader = new (window as any).NDEFReader();
      await reader.scan();
      return new Promise((resolve) => {
        reader.onreading = (event: any) => {
          const { serialNumber } = event;
          const records = event.message?.records ?? [];
          const data = records.map((r: any) => new TextDecoder().decode(r.data)).join('');
          resolve({
            tagId: serialNumber ?? data,
            serialNumber,
            data: data || undefined,
          });
        };
        setTimeout(() => resolve(null), 10000);
      });
    }
    return null;
  } catch {
    return null;
  }
}

// ─── Biometric Authentication ─────────────────────────────────────────

export interface BiometricResult {
  verified: boolean;
  error?: string;
}

/** Trigger biometric authentication (fingerprint / face) */
export async function authenticateBiometric(): Promise<BiometricResult> {
  try {
    if (isNativePlatform()) {
      const { BiometricAuth } = await import('@aparajita/capacitor-biometric-auth');
      const result = await BiometricAuth.authenticate({
        reason: 'Please verify your identity to access OpexMES',
        iosFallbackTitle: 'Use passcode',
        androidTitle: 'Authentication required',
        androidSubtitle: 'Please verify to continue',
      });
      return { verified: result.status === 'success' };
    }

    // Web fallback: WebAuthn / platform authenticator
    if (window.PublicKeyCredential) {
      const challenge = new Uint8Array(32);
      crypto.getRandomValues(challenge);
      const credential = await navigator.credentials.get({
        publicKey: {
          challenge,
          rpId: window.location.hostname,
          allowCredentials: [],
          userVerification: 'required',
          timeout: 30000,
        },
      } as any);
      return { verified: !!credential };
    }
    return { verified: false, error: 'Biometric not supported' };
  } catch {
    return { verified: false };
  }
}

// ─── Offline Storage ─────────────────────────────────────────────────

export interface OfflineQueueItem {
  id: string;
  type: string;
  url: string;
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: any;
  createdAt: number;
  retries: number;
  maxRetries: number;
}

const QUEUE_KEY = 'offline-sync-queue';

/** Add an operation to the offline sync queue */
export async function queueOfflineOperation(
  item: Omit<OfflineQueueItem, 'createdAt' | 'retries' | 'maxRetries'>,
): Promise<void> {
  try {
    const { value } = await Preferences.get({ key: QUEUE_KEY });
    const queue: OfflineQueueItem[] = value ? JSON.parse(value) : [];
    queue.push({
      ...item,
      createdAt: Date.now(),
      retries: 0,
      maxRetries: 10,
    });
    await Preferences.set({ key: QUEUE_KEY, value: JSON.stringify(queue) });
  } catch (error) {
    console.error('Failed to queue offline operation:', error);
  }
}

/** Get all pending offline operations */
export async function getOfflineQueue(): Promise<OfflineQueueItem[]> {
  try {
    const { value } = await Preferences.get({ key: QUEUE_KEY });
    return value ? JSON.parse(value) : [];
  } catch {
    return [];
  }
}

/** Process the offline sync queue */
export async function processOfflineQueue(): Promise<{ processed: number; failed: number }> {
  const queue = await getOfflineQueue();
  let processed = 0;
  let failed = 0;

  for (const item of queue) {
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      const res = await fetch(item.url, {
        method: item.method,
        headers,
        body: item.body ? JSON.stringify(item.body) : undefined,
      });
      if (res.ok) processed++;
      else {
        item.retries++;
        if (item.retries >= item.maxRetries) failed++;
      }
    } catch {
      item.retries++;
      if (item.retries >= item.maxRetries) failed++;
    }
  }

  // Keep only items that haven't exceeded max retries
  const remaining = queue.filter((item) => item.retries < item.maxRetries);
  await Preferences.set({ key: QUEUE_KEY, value: JSON.stringify(remaining) });

  return { processed, failed };
}
